import React, { useCallback, useMemo } from 'react';

import i18n from '@nextroll/ar-i18n';
import { BUSINESS_UNIT_ROLLWORKS, Logo } from '@nextroll/ar-style-base';
import { Link } from '@nextroll/ar-container-app';
import { useContainerContext } from '@nextroll/ar-container-context';

import { useRoflStore } from 'store/roflStore';

import { WORKFLOW_TRIGGER_CONTACT_LIST_STATUS } from '../../../../types/rofl';
import { getContactListDetailsUrl, getCreateContactListUrl } from '../../../../util/urls';
import { NodePanelDropDown } from '../../../basic/NodePanelDropDown/NodePanelDropDown';
import { IContactListOption } from '../ContactTriggerNode.type';
import { CONTACT_TRIGGER_ERROR } from '../ContactTriggerNode.constant';

/**
 * Custom dropdown header when there is no data, i.e. data response is empty.
 */
const DropDownHeaderNoData = ({ advertisableEid }: { advertisableEid?: string }) => (
    <>
        <p>{CONTACT_TRIGGER_ERROR.NO_DATA}</p>
        <Link
            to={getCreateContactListUrl(advertisableEid)}
            openNewWindow
            rel='noopener noreferrer'
        >
            {i18n.gettext('Create Contact List')}
            &nbsp;
            <i className='ar-icon ar-icon-fw fa-external-link-alt' />
        </Link>
    </>
);

/**
 * Custom dropdown header when error occurred when fetching dropdown options.
 */
const DropDownHeaderError = () => <span>{CONTACT_TRIGGER_ERROR.BASIC}</span>;

interface IProps {
    // getter and setter of the selected contact
    contactList?: IContactListOption;
    onUpdateContactList?: (selectedContactList: IContactListOption) => void;

    // variables used in fetching the contact lists
    loading: boolean;
    data?: IContactListOption[];
    warning?: string;
    error?: string;

    // other callback(s)
    onRefresh?: () => void;
}

export const ListMembershipPanelDropDownSection = (props: IProps) => {
    const { contactList, onUpdateContactList, loading, data, warning, error, onRefresh } = props;

    const { advertisable: { eid: advertisableEid } = {} } = useContainerContext();

    const shouldShowRequiredError = useRoflStore(state => state.shouldShowRequiredError);

    const header = useMemo(() => {
        if ((data || []).length <= 0) {
            return error ? <DropDownHeaderError /> : <DropDownHeaderNoData advertisableEid={advertisableEid} />;
        }
        return undefined;
    }, [error, data, advertisableEid]);

    const errorDisplayed = useMemo(() => {
        if (error) {
            return error;
        }
        if (shouldShowRequiredError && !props.contactList) {
            return i18n.gettext('Required to select a Contact List.');
        }
    }, [shouldShowRequiredError, props.contactList, error]);

    const handleSelectOption = useCallback(
        (option: IContactListOption) => onUpdateContactList?.(option), [onUpdateContactList]
    );

    const handleSearch = useCallback((search: string, option: IContactListOption) => !search
        || (option.name || '').toLowerCase().includes(search.toLowerCase()), []);

    return (
        <div className='workflow-contact-trigger-node-panel-select-container'>
            <span>
                <Logo
                    i18n={i18n}
                    businessUnit={BUSINESS_UNIT_ROLLWORKS}
                    wordmark={false}
                    height='16px'
                    disableRollWorksTheme
                />
                &nbsp;&nbsp;
                <h4>{i18n.gettext('Contact is member of Enhanced Contact List')}</h4>
            </span>

            <NodePanelDropDown<IContactListOption>
                data={data}
                loading={loading}
                warning={warning}
                error={errorDisplayed}
                header={header}
                onSearch={handleSearch}
                onSelect={handleSelectOption}
                renderOption={option => option.name}
                renderSelected={option => option.name === contactList?.name}
                renderWarning={option => option.status === WORKFLOW_TRIGGER_CONTACT_LIST_STATUS.STOPPED_SYNCING}
                allowRefresh
                onRefresh={onRefresh}
            />

            {/* TODO: insert real href here */}
            {props.contactList && (
                <Link
                    to={getContactListDetailsUrl(props.contactList.eid, advertisableEid)}
                    openNewWindow
                    rel='noopener noreferrer'
                >
                    {i18n.gettext('View Contact List')}
                    &nbsp;
                    <i className='ar-icon ar-icon-fw fa-external-link-alt' />
                </Link>
            )}
        </div>
    );
};
